"use client";

import { AvailabilitySearchParameters } from "@/types/AvailabilitySearchParameters";
import constructAvailabilitySearchParametersQuery from "@/utils/constructAvailabilitySearchParametersQuery";
import { DateTime } from "luxon";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import LeftArrow from "../../icons/LeftArrow";
import RightArrow from "../../icons/RightArrow";
import ButtonIcon from "../ButtonIcon";
import Calendar from "../Calendar";
import "./index.scss";

const AvailabilityCalendar = ({
  availabilitySearchParameters,
  onChange,
  unavailableDates = [],
}: {
  availabilitySearchParameters: AvailabilitySearchParameters;
  onChange?: (startDate: string, endDate: string) => void;
  unavailableDates?: string[];
}) => {
  const router = useRouter();
  const todayLuxon = DateTime.now();

  const [currentMonth, setCurrentMonth] = useState<number>(
    !!availabilitySearchParameters.startDate
      ? DateTime.fromISO(availabilitySearchParameters.startDate).month
      : todayLuxon.month
  );
  const [currentYear, setCurrentYear] = useState<number>(
    !!availabilitySearchParameters.startDate
      ? DateTime.fromISO(availabilitySearchParameters.startDate).year
      : todayLuxon.year
  );
  const [selectedStartDate, setSelectedStartDate] = useState(
    availabilitySearchParameters.startDate
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    availabilitySearchParameters.endDate
  );
  const [focusField, setFocusField] = useState<"START_DATE" | "END_DATE">(
    "START_DATE"
  );

  useEffect(() => {
    if (
      !!availabilitySearchParameters.startDate &&
      !!availabilitySearchParameters.endDate
    ) {
      const startDateLuxon = DateTime.fromISO(
        availabilitySearchParameters.startDate
      );
      const endDateLuxon = DateTime.fromISO(
        availabilitySearchParameters.endDate
      );

      setSelectedStartDate(startDateLuxon.toISODate()!);
      setSelectedEndDate(endDateLuxon.toISODate()!);
      setCurrentMonth(startDateLuxon.month);
      setCurrentYear(startDateLuxon.year);
    } else {
      setSelectedStartDate("");
      setSelectedEndDate("");
      setCurrentMonth(todayLuxon.month);
      setCurrentYear(todayLuxon.year);
    }
  }, [availabilitySearchParameters]);

  const nextMonth = currentMonth <= 11 ? currentMonth + 1 : 1;
  const nextYear = currentMonth <= 11 ? currentYear : currentYear + 1;

  const isPreviousMonthButtonDisabled =
    todayLuxon.month === currentMonth && todayLuxon.year === currentYear;

  const isNextMonthButtonDisabled =
    todayLuxon.month === currentMonth && todayLuxon.year + 2 === currentYear;

  const handleOnClick = (date: string) => {
    if (focusField === "START_DATE") {
      setSelectedStartDate(date);
      setSelectedEndDate("");
      setFocusField("END_DATE");
    } else if (focusField === "END_DATE" && date <= selectedStartDate) {
      setSelectedStartDate(date);
      setSelectedEndDate("");
      setFocusField("END_DATE");
    } else {
      setSelectedEndDate(date);
      setFocusField("START_DATE");
      if (onChange) {
        onChange(selectedStartDate!, date);
      } else {
        const updatedAvailabilitySearchParameters = {
          ...availabilitySearchParameters,
          startDate: selectedStartDate,
          endDate: date,
        };

        const availabilitySearchParametersQuery =
          constructAvailabilitySearchParametersQuery(
            updatedAvailabilitySearchParameters
          );

        const url = `?${availabilitySearchParametersQuery}`;
        router.replace(url, { scroll: false });
      }
    }
  };

  return (
    <div className="component availability-calendar">
      <div className="availability-calendar-inner-wrapper">
        <div className="calendar-controls">
          <ButtonIcon
            className="previous-button"
            type="button"
            aria-label="Previous month"
            onClick={() => {
              if (isPreviousMonthButtonDisabled) {
                // Do nothing
              } else {
                if (currentMonth > 1) {
                  setCurrentMonth(currentMonth - 1);
                } else {
                  setCurrentMonth(12);
                  setCurrentYear(currentYear - 1);
                }
              }
            }}
          >
            <LeftArrow />
          </ButtonIcon>
          <ButtonIcon
            className="next-button"
            type="button"
            aria-label="Next month"
            onClick={() => {
              if (isNextMonthButtonDisabled) {
                // Do nothing
              } else {
                if (currentMonth < 12) {
                  setCurrentMonth(currentMonth + 1);
                } else {
                  setCurrentMonth(1);
                  setCurrentYear(currentYear + 1);
                }
              }
            }}
          >
            <RightArrow />
          </ButtonIcon>
        </div>
        <div className="calendar-window">
          <Calendar
            month={currentMonth}
            year={currentYear}
            today={todayLuxon.toISODate()!}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            onClick={handleOnClick}
            unavailableDates={unavailableDates}
          />
          <Calendar
            month={nextMonth}
            year={nextYear}
            today={todayLuxon.toISODate()!}
            startDate={selectedStartDate}
            endDate={selectedEndDate}
            onClick={handleOnClick}
            unavailableDates={unavailableDates}
          />
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
